/** Vendors / Vendors - who bills this company, with what that vendor's meters add up to (ADR-0212).
 *
 * A vendor is the company's own note of a party that bills it (ADR-0062). It is **not a supplier**
 * (ADR-0040): a supplier is another company's workspace and names never cross, which the info control
 * beside the title says. The figures beside a vendor are read **through its meters**; a document is
 * never matched to a vendor by what it says (SEC-05), so no column here counts documents.
 *
 * Tiles are the register's own counts; the table's numbers are the server's (`/sources/vendors/summary`).
 * A vendor with no reading shows a dash, not a zero. Row click opens the vendor's accounts in a drawer.
 */

import { Storefront } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useCoverage, useRegisters } from "../../api/sources";
import type { RegisterVendor, SupplyAccount } from "../../api/sources";
import { useVendorSummary } from "../../api/vendorSummary";
import type { VendorSummaryRow } from "../../api/vendorSummary";
import { ReadFailed } from "../../components/Loaded";
import { AddImportActions } from "../../components/ui/AddImportActions";
import { DashboardBand } from "../../components/ui/DashboardBand";
import { Drawer } from "../../components/ui/Drawer";
import { EmptyAction } from "../../components/ui/EmptyAction";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { StatusChip } from "../../components/ui/StatusChip";
import type { SortState } from "../../components/ui/sortRows";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { shortDate, tonnes } from "../../lib/format";
import { TopVendorsCard, VendorFiguresCard } from "../bands/VendorCards";
import { AccountRow } from "../SourcesRegisters";
import type { Screen } from "../useScreen";
import { OpenFigures, count, matches, ordered, usePlaceLabel } from "../tableKit";
import { MAY_RECORD } from "./roles";
import styles from "./Vendors.module.css";
import { AddAccountDrawer, AddVendorDrawer } from "./VendorForms";
import { EditVendorDrawer } from "./VendorEdit";

const STATUS_FILTER: FilterDef = {
  id: "status",
  label: "Status",
  mode: "single",
  allLabel: "All vendors",
  options: [
    { value: "active", label: "Active" },
    { value: "retired", label: "Retired" },
  ],
};

interface Line {
  vendor: RegisterVendor;
  summary: VendorSummaryRow | undefined;
  meters: number;
}

export function VendorsTab({ screen }: { screen: Screen }) {
  const label = usePlaceLabel();
  const role = useMe().data?.role;
  const registers = useRegisters();
  const window = screen.window;
  const summary = useVendorSummary(window?.period_start, window?.period_end, screen.method);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<"vendor" | "account" | null>(null);
  const [editing, setEditing] = useState(false);
  const coverage = useCoverage(window?.period_start, window?.period_end, Boolean(window) && open !== null);

  const mayRecord = role !== undefined && MAY_RECORD.includes(role);
  const data = registers.data;
  const bySummary = new Map((summary.data?.vendors ?? []).map((row) => [row.id, row]));
  const lines: Line[] = (data?.vendors ?? []).map((vendor) => ({
    vendor,
    summary: bySummary.get(vendor.id),
    meters: new Set(
      data?.accounts.filter((a) => a.vendor_id === vendor.id).flatMap((a) => a.meters.map((m) => m.id)),
    ).size,
  }));
  const shown = ordered(
    lines.filter(
      (line) =>
        matches(filters.search, line.vendor.name) &&
        (filters.values.status?.[0] === undefined ||
          (filters.values.status[0] === "retired") === line.vendor.is_retired),
    ),
    sort,
    {
      vendor: (l) => l.vendor.name,
      accounts: (l) => l.vendor.account_count,
      meters: (l) => l.meters,
      figures: (l) => l.summary?.figure_count,
      last: (l) => l.summary?.last_bill_end,
      tco2e: (l) => (l.summary?.kgco2e ? Number(l.summary.kgco2e) : null),
    },
  );

  const columns: GridColumn<Line>[] = [
    {
      id: "vendor",
      header: "Vendor",
      sortable: true,
      cell: (l) =>
        l.vendor.is_retired ? (
          <>
            {l.vendor.name}{" "}
            <StatusChip tone="neutral" size="sm" testId="vendor-retired">
              Retired
            </StatusChip>
          </>
        ) : (
          l.vendor.name
        ),
    },
    { id: "accounts", header: "Accounts", numeric: true, sortable: true, cell: (l) => count(l.vendor.account_count) },
    { id: "meters", header: "Meters", numeric: true, sortable: true, cell: (l) => count(l.meters) },
    {
      id: "figures",
      header: "Figures",
      numeric: true,
      sortable: true,
      cell: (l) => (l.summary ? count(l.summary.figure_count) : "—"),
    },
    {
      id: "last",
      header: "Last bill",
      sortable: true,
      cell: (l) => (l.summary?.last_bill_end ? shortDate(l.summary.last_bill_end) : "—"),
    },
    {
      id: "tco2e",
      header: "tCO2e",
      numeric: true,
      sortable: true,
      cell: (l) => (
        <OpenFigures
          screen={screen}
          nodeKey={`vendor-${l.vendor.id}`}
          value={l.summary?.kgco2e ? tonnes(l.summary.kgco2e) : "—"}
          name={l.vendor.name}
        />
      ),
    },
  ];

  const failed = registers.error ?? (window ? summary.error : null);
  // Not one vendor yet: no chart and no filter line, only the designed empty state under the tiles.
  const nothingYet = data !== undefined && data.vendors.length === 0;
  // The charts also wait for the summary to agree (it is a separate read): a summary that failed keeps
  // its two failure cards, and one that names vendors the register lacks is not an empty workspace.
  const noCharts = nothingYet && (!window || summary.data?.vendors.length === 0);
  const opened = data?.vendors.find((vendor) => vendor.id === open);
  const accountsOf = (id: string): SupplyAccount[] => data?.accounts.filter((a) => a.vendor_id === id) ?? [];

  return (
    <>
      <TablePage
        icon={Storefront}
        title="Vendors"
        label={label}
        actions={
          <>
            <InfoPopover infoKey="vendors.vendor-vs-supplier" />
            <AddImportActions
              role={mayRecord ? role : undefined}
              addLabel="Add vendor"
              onAdd={() => setAdding("vendor")}
            />
          </>
        }
        band={
          data ? (
            <DashboardBand
              label="Vendors dashboard"
              tiles={
                <TileStrip label="Vendors summary">
                  <Tile value={count(data.vendors.filter((v) => !v.is_retired).length)} label="Vendors" />
                  <Tile value={count(data.accounts.length)} label="Accounts" />
                  <Tile
                    value={count(data.not_recorded.length)}
                    label="Meters without an account"
                    infoKey="vendors.not-recorded"
                  />
                </TileStrip>
              }
              charts={
                !window
                  ? undefined
                  : [
                <TopVendorsCard
                  key="top"
                  read={summary}
                  summary={summary.data}
                  onOpen={(key) => screen.openBreakdown(key)}
                />,
                <VendorFiguresCard
                  key="figures"
                  read={summary}
                  summary={summary.data}
                  onOpen={(key) => screen.openBreakdown(key)}
                />,
                  ]
              }
            />
          ) : undefined
        }
        nothingYet={noCharts}
        filters={
          nothingYet ? undefined : (
            <FilterLine
              filters={[STATUS_FILTER]}
              state={filters}
              onChange={setFilters}
              searchLabel="Search vendors"
              resultCount={data ? { shown: shown.length, total: lines.length, noun: "vendors" } : undefined}
            />
          )
        }
        table={
          data ? (
            <GridTable
              caption="Vendors"
              columns={columns}
              rows={shown}
              rowKey={(l) => l.vendor.id}
              rowLabel={(l) => l.vendor.name}
              sort={sort}
              onSortChange={setSort}
              onRowOpen={(l) => setOpen(l.vendor.id)}
              empty={
                nothingYet ? (
                  <EmptyState
                    icon={Storefront}
                    title="No vendors yet"
                    compact
                    testId="vendors-empty"
                    action={
                      <EmptyAction
                        label="Add vendor"
                        allowed={role === undefined ? undefined : mayRecord}
                        reason="An Admin or Approver adds vendors."
                        onClick={() => setAdding("vendor")}
                        testId="vendors-empty-add"
                      />
                    }
                  >
                    Vendors are the companies that bill you.
                  </EmptyState>
                ) : undefined
              }
              testId="vendors-table"
            />
          ) : undefined
        }
        state={
          failed ? (
            <ReadFailed
              of="The vendors"
              error={failed}
              onRetry={() => {
                void registers.refetch();
                void summary.refetch();
              }}
            />
          ) : !data ? (
            <GridTable
              caption="Vendors"
              columns={[]}
              rows={[]}
              rowKey={(l: Line) => l.vendor.id}
              loading
              testId="vendors-loading"
            />
          ) : undefined
        }
      />
      <Drawer
        open={opened !== undefined}
        onClose={() => setOpen(null)}
        title={opened?.name ?? "Vendor"}
        width="lg"
        footer={
          mayRecord ? (
            <>
              <button type="button" className={styles.ghost} onClick={() => setEditing(true)} data-testid="vendor-edit">
                Edit vendor
              </button>
              {opened?.is_retired ? null : (
                <button type="button" className={styles.primary} onClick={() => setAdding("account")} data-testid="vendor-add-account">
                  Add account
                </button>
              )}
            </>
          ) : undefined
        }
      >
        {opened && coverage.error ? (
          <ReadFailed
            of="The expected bills"
            error={coverage.error}
            onRetry={() => void coverage.refetch()}
          />
        ) : null}
        {opened && data ? (
          accountsOf(opened.id).length === 0 ? (
            <EmptyState title="No accounts yet" compact testId="vendor-no-accounts" />
          ) : (
            accountsOf(opened.id).map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                available={[
                  ...data.not_recorded,
                  ...data.accounts
                    .filter((other) => other.id !== account.id)
                    .flatMap((other) => other.meters.filter((m) => !m.operational_to)),
                ]}
                mayDeclare={mayRecord}
                streams={coverage.data?.sources ?? []}
              />
            ))
          )
        ) : null}
      </Drawer>
      <EditVendorDrawer vendor={editing ? opened : undefined} onClose={() => setEditing(false)} />
      <AddVendorDrawer open={adding === "vendor"} onClose={() => setAdding(null)} />
      <AddAccountDrawer
        open={adding === "account"}
        onClose={() => setAdding(null)}
        vendors={(data?.vendors ?? []).filter((v) => !v.is_retired)}
        vendorId={open ?? undefined}
      />
    </>
  );
}
